import React from "react";
import { isoFromDate } from "./MonthGrid";
import { reminderStatus, REMINDER_DOT_CLASS } from "@/lib/reminder-status";
import { computeClassBreakdown, studentCalendarLabelWide, isExamAppointment, unavailColor, examTextClass } from "@/lib/utils-app";

const WEEKDAYS = ["Lun", "Mar", "Mié", "Jue", "Vie", "Sáb", "Dom"];

const STATUS_DOT = {
  pending: "bg-amber-500",
  confirmed: "bg-green-500",
  cancelled: "bg-red-500",
  change_requested: "bg-blue-500",
};

function DayColumn({ date, isToday, isSelected, appointments, unavailabilities, onSelect }) {
  const iso = isoFromDate(date);
  const weekday = WEEKDAYS[(date.getDay() + 6) % 7];
  const unavail = unavailabilities || [];
  // V1.43 · Día completo bloqueado (vacaciones/ausencia).
  const allDayBlock = unavail.find((u) => u.all_day) || null;
  const hourlyUnavail = unavail.filter((u) => !u.all_day);
  const reminderCounts = appointments.reduce(
    (acc, a) => {
      const r = reminderStatus(a);
      if (r === "red") acc.red += 1;
      else if (r === "orange") acc.orange += 1;
      return acc;
    },
    { red: 0, orange: 0 }
  );
  return (
    <button
      data-testid={`cal-day-${iso}`}
      onClick={() => onSelect(iso)}
      className={
        "text-left flex flex-col rounded-xl border transition-colors overflow-hidden relative " +
        (allDayBlock
          ? "border-neutral-300 bg-neutral-200/70 hover:bg-neutral-200"
          : isSelected
          ? "border-orange-400 bg-orange-50 shadow-sm"
          : "border-neutral-200 bg-white hover:border-neutral-300 hover:bg-neutral-50")
      }
      data-all-day-block={allDayBlock ? "1" : "0"}
    >
      {(reminderCounts.red > 0 || reminderCounts.orange > 0) && (
        <span
          data-testid={`cal-day-reminder-badge-${iso}`}
          className={
            "absolute top-1 right-1 z-10 h-2.5 w-2.5 rounded-full ring-2 ring-white " +
            (reminderCounts.red > 0 ? "bg-red-500 animate-pulse" : "bg-orange-500")
          }
          title={
            reminderCounts.red > 0
              ? `${reminderCounts.red} recordatorio(s) urgentes (<24h)`
              : `${reminderCounts.orange} recordatorio(s) pendientes (24-48h)`
          }
        />
      )}
      <div
        className={
          "flex items-center justify-between px-2.5 py-1.5 border-b " +
          (isToday
            ? "bg-orange-500 text-white border-orange-500"
            : isSelected
            ? "bg-orange-100 text-orange-900 border-orange-200"
            : "bg-neutral-50 text-neutral-700 border-neutral-200")
        }
      >
        <div className="text-[11px] font-semibold uppercase tracking-wide">{weekday}</div>
        <div className="text-sm font-bold tabular-nums">{date.getDate()}</div>
      </div>
      <div className="flex-1 p-1.5 space-y-1 min-h-[110px]">
        {/* V1.43 · Banner "Día completo" — vacaciones/ausencia del profesor */}
        {allDayBlock && (
          <div
            className={"flex items-center gap-1 text-[10px] leading-tight truncate rounded-md px-1.5 py-1 font-semibold " + unavailColor(allDayBlock.color).chip}
            title={`${allDayBlock.teacher || ""} · ${allDayBlock.title || "No disponible"}`}
            data-testid={`cal-unavail-allday-${allDayBlock.id}`}
            data-color={allDayBlock.color || "gris"}
          >
            <span className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + unavailColor(allDayBlock.color).dot} />
            <span className="truncate">{allDayBlock.title || "No disponible"}</span>
          </div>
        )}
        {/* V1.38 · Bloques "No disponible" pintados arriba del listado de
            sesiones para que se detecten al golpe de vista. Título con
            privacidad aplicada desde backend. V1.69 · color configurable. */}
        {!allDayBlock && hourlyUnavail.slice(0, 4).map((u) => (
          <div
            key={u.id}
            className={"flex items-center gap-1 text-[10px] leading-tight truncate rounded-md px-1.5 py-1 font-semibold " + unavailColor(u.color).chip}
            title={`${u.time}${u.end_time ? `–${u.end_time}` : ""} · ${u.teacher || ""} · ${u.title || "No disponible"}`}
            data-testid={`cal-unavail-line-${u.id}`}
            data-color={u.color || "gris"}
          >
            <span className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + unavailColor(u.color).dot} />
            <span className="font-semibold tabular-nums">
              {u.time}
              {u.end_time && (
                <span className={"font-normal " + unavailColor(u.color).muted}>–{u.end_time}</span>
              )}
            </span>
            <span className="italic truncate opacity-90">
              {u.title || "No disponible"}
            </span>
          </div>
        ))}
        {appointments.length === 0 && unavail.length === 0 ? (
          <div className="text-[10px] text-neutral-400 italic px-1">Sin citas</div>
        ) : allDayBlock ? (
          appointments.length > 0 && (
            <div className="text-[10px] text-neutral-500 italic px-1">
              {appointments.length} sesión{appointments.length === 1 ? "" : "es"} solapada{appointments.length === 1 ? "" : "s"}
            </div>
          )
        ) : (
          appointments.slice(0, 6).map((a) => {
            const rem = reminderStatus(a);
            const isExam = isExamAppointment(a.appointment_type);
            const { numClasses } = isExam
              ? { numClasses: 0 }
              : computeClassBreakdown(a.time, a.end_time);
            const wideName = studentCalendarLabelWide(a.student);
            return (
              <div
                key={a.id}
                className="flex items-center gap-1 text-[10px] leading-tight truncate rounded-md bg-neutral-50 px-1.5 py-1"
                title={`${a.time}${a.end_time ? `–${a.end_time}` : ""}${numClasses > 0 ? ` (${numClasses})` : ""} · ${a.student?.name || ""}${rem ? ` · ${rem === "red" ? "Recordatorio <24h" : rem === "orange" ? "Recordatorio 24-48h" : "Recordatorio enviado"}` : ""}`}
                data-testid={`cal-appt-line-${a.id}`}
                data-reminder-status={rem || "none"}
              >
                <span
                  className={
                    "inline-block h-1.5 w-1.5 rounded-full shrink-0 " +
                    (STATUS_DOT[a.status] || "bg-neutral-400")
                  }
                />
                {rem && (
                  <span
                    data-testid={`cal-reminder-dot-${a.id}`}
                    className={"inline-block h-1.5 w-1.5 rounded-full shrink-0 " + REMINDER_DOT_CLASS[rem]}
                  />
                )}
                <span className="font-semibold tabular-nums">
                  {a.time}
                  {a.end_time && (
                    <span className="text-neutral-400 font-normal">–{a.end_time}</span>
                  )}
                </span>
                <span className={(isExam ? examTextClass(a.appointment_type) + " font-semibold" : "text-neutral-500") + " truncate"}>
                  {wideName}
                </span>
                {numClasses > 0 && (
                  <span className="text-orange-600 font-semibold shrink-0">({numClasses})</span>
                )}
              </div>
            );
          })
        )}
        {!allDayBlock && appointments.length > 6 && (
          <div className="text-[10px] text-neutral-400 px-1">+{appointments.length - 6} más</div>
        )}
      </div>
    </button>
  );
}

export default function DayColumnsGrid({ startDate, days, byDate, unavailByDate, todayIso, selectedDate, onSelect }) {
  const dates = [];
  for (let i = 0; i < days; i++) {
    const d = new Date(startDate);
    d.setDate(startDate.getDate() + i);
    dates.push(d);
  }
  // Always 7 columns; a 14-day view naturally wraps to two rows of 7.
  return (
    <div className="grid gap-2 grid-cols-7">
      {dates.map((d) => {
        const iso = isoFromDate(d);
        return (
          <DayColumn
            key={iso}
            date={d}
            isToday={iso === todayIso}
            isSelected={iso === selectedDate}
            appointments={byDate[iso] || []}
            unavailabilities={(unavailByDate && unavailByDate[iso]) || []}
            onSelect={onSelect}
          />
        );
      })}
    </div>
  );
}
